import { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useQuery } from "../../lib/useQuery";
import { supabase } from "../../lib/supabase";
import { useToast } from "../Toast";
import LoadingSkeleton from "../LoadingSkeleton";
import SimulationsList from "./SimulationsList";
import SimulationEditor from "./SimulationEditor";

const DEFAULT_GASTOS = [
  { concepto: "Alquiler", tipo: "fijos", importe: "" },
  { concepto: "Autónomo", tipo: "fijos", importe: "" },
  { concepto: "Seguro", tipo: "fijos", importe: "" },
  { concepto: "Gestoría", tipo: "fijos", importe: "" },
  { concepto: "Sueldo Gestor", tipo: "fijos", importe: "" },
  { concepto: "Crédito", tipo: "fijos", importe: "" },
  { concepto: "Limpieza", tipo: "fijos", importe: "" },
  { concepto: "Marketing", tipo: "fijos", importe: "" },
  { concepto: "Imprevistos", tipo: "fijos", importe: "" },
  { concepto: "App", tipo: "fijos", importe: "" },
  { concepto: "Sueldo Entrenador", tipo: "fijos", importe: "" },
  { concepto: "Internet, Luz, Agua", tipo: "fijos", importe: "" },
];

const DEFAULT_SERVICIO_GRUPO = {
  nombre: "Sesiones Grupales",
  iva: 21,
  personas: 15,
  sesiones_semana: 35,
  minutos: 60,
  cuota: 45,
  horas_prof: 30,
  sesiones_cliente: 2.2,
  clientes_actuales: "",
  pct_deseado: 85,
};

const DEFAULT_SERVICIO_INDIVIDUAL = {
  nombre: "Entrenamiento Personal",
  iva: 21,
  sesiones_semana: "",
  minutos: 60,
  cuota: 45,
  horas_prof: 30,
};

const DEFAULT_STATE = {
  name: "Nueva simulación",
  servicios_grupo: [{ ...DEFAULT_SERVICIO_GRUPO }],
  servicios_individuales: [
    { ...DEFAULT_SERVICIO_INDIVIDUAL, nombre: "Entrenamiento Personal", iva: 21 },
    { ...DEFAULT_SERVICIO_INDIVIDUAL, nombre: "Fisioterapia", iva: 0, horas_prof: 35 },
    { ...DEFAULT_SERVICIO_INDIVIDUAL, nombre: "Nutrición", iva: 0, horas_prof: 35 },
  ],
  empleados: [],
  gastos: [...DEFAULT_GASTOS],
  seg_social_pct: 32.5,
  visitas_semana: 2.2,
  semanas_mes: 4.2,
};

export { DEFAULT_SERVICIO_GRUPO, DEFAULT_SERVICIO_INDIVIDUAL };

export default function PreciosOcupacion({ onBack }) {
  const { user } = useAuth();
  const { showError, showSuccess } = useToast();
  const [view, setView] = useState("list");
  const [state, setState] = useState({ ...DEFAULT_STATE });
  const [currentId, setCurrentId] = useState(null);
  const [saving, setSaving] = useState(false);

  const { data: simulations, loading, refetch } = useQuery(async () => {
    const { data, error } = await supabase
      .from("simulations")
      .select("id, name, created_at")
      .eq("student_id", user.id)
      .order("updated_at", { ascending: false });
    if (error) throw error;
    return data;
  }, [user.id]);

  const loadSimulation = (data) => {
    let servicios_grupo = data.servicios_grupo;
    let servicios_individuales = data.servicios_individuales;

    if (!Array.isArray(servicios_grupo) || servicios_grupo.length === 0) {
      servicios_grupo = [{
        nombre: "Sesiones Grupales",
        iva: (data.grupo_iva || 0.21) * 100,
        personas: data.grupo_personas || 15,
        sesiones_semana: data.grupo_sesiones_semana || 35,
        minutos: data.grupo_minutos || 60,
        cuota: data.grupo_cuota || 45,
        horas_prof: data.grupo_horas_profesional || 30,
        sesiones_cliente: data.visitas_semana || 2.2,
        clientes_actuales: "",
        pct_deseado: 85,
      }];
    } else {
      servicios_grupo = servicios_grupo.map(s => ({
        ...s,
        iva: (s.iva || 0) * 100,
        sesiones_cliente: s.sesiones_cliente || data.visitas_semana || 2.2,
        clientes_actuales: s.clientes_actuales ?? "",
        pct_deseado: s.pct_deseado ?? 85,
      }));
    }

    if (!Array.isArray(servicios_individuales) || servicios_individuales.length === 0) {
      servicios_individuales = [
        { nombre: "Entrenamiento Personal", iva: (data.ind_personal_iva || 0.21) * 100, sesiones_semana: data.ind_personal_sesiones || "", minutos: data.ind_personal_minutos || 60, cuota: data.ind_personal_cuota || 45, horas_prof: data.ind_personal_horas_prof || 30 },
        { nombre: "Fisioterapia", iva: (data.ind_fisio_iva || 0) * 100, sesiones_semana: data.ind_fisio_sesiones || "", minutos: data.ind_fisio_minutos || 60, cuota: data.ind_fisio_cuota || 45, horas_prof: data.ind_fisio_horas_prof || 35 },
        { nombre: "Nutrición", iva: (data.ind_nutri_iva || 0) * 100, sesiones_semana: data.ind_nutri_sesiones || "", minutos: data.ind_nutri_minutos || 60, cuota: data.ind_nutri_cuota || 45, horas_prof: data.ind_nutri_horas_prof || 35 },
      ];
    } else {
      servicios_individuales = servicios_individuales.map(s => ({
        ...s,
        iva: (s.iva || 0) * 100,
      }));
    }

    return {
      name: data.name,
      servicios_grupo,
      servicios_individuales,
      empleados: data.empleados || [],
      gastos: Array.isArray(data.gastos) ? data.gastos : [...DEFAULT_GASTOS],
      seg_social_pct: (data.seg_social_pct || 0.325) * 100,
      visitas_semana: data.visitas_semana || 2.2,
      semanas_mes: data.semanas_mes || 4.2,
    };
  };

  const handleNew = () => {
    setCurrentId(null);
    setState({
      ...DEFAULT_STATE,
      servicios_grupo: [{ ...DEFAULT_SERVICIO_GRUPO }],
      servicios_individuales: [
        { ...DEFAULT_SERVICIO_INDIVIDUAL, nombre: "Entrenamiento Personal", iva: 21 },
        { ...DEFAULT_SERVICIO_INDIVIDUAL, nombre: "Fisioterapia", iva: 0, horas_prof: 35 },
        { ...DEFAULT_SERVICIO_INDIVIDUAL, nombre: "Nutrición", iva: 0, horas_prof: 35 },
      ],
      gastos: [...DEFAULT_GASTOS],
    });
    setView("edit");
  };

  const handleSelect = async (id) => {
    const { data } = await supabase.from("simulations").select("*").eq("id", id).single();
    if (data) {
      setCurrentId(id);
      setState(loadSimulation(data));
      setView("edit");
    }
  };

  const handleDuplicate = async (id) => {
    const { data } = await supabase.from("simulations").select("*").eq("id", id).single();
    if (data) {
      setCurrentId(null);
      setState({ ...loadSimulation(data), name: `${data.name} (copia)` });
      setView("edit");
    }
  };

  const handleDelete = async (id) => {
    const { error } = await supabase.from("simulations").delete().eq("id", id);
    if (error) {
      showError("No se pudo eliminar la simulación");
      return;
    }
    refetch();
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const num = (v) => (typeof v === "number" ? v : parseFloat(v) || 0);
      
      const servicios_grupo = state.servicios_grupo.map(s => ({
        nombre: s.nombre || "Servicio Grupo",
        iva: num(s.iva) / 100,
        personas: num(s.personas),
        sesiones_semana: num(s.sesiones_semana),
        minutos: num(s.minutos),
        cuota: num(s.cuota),
        horas_prof: num(s.horas_prof),
        sesiones_cliente: num(s.sesiones_cliente) || 2.2,
        clientes_actuales: num(s.clientes_actuales),
        pct_deseado: num(s.pct_deseado),
      }));

      const servicios_individuales = state.servicios_individuales.map(s => ({
        nombre: s.nombre || "Servicio Individual",
        iva: num(s.iva) / 100,
        sesiones_semana: num(s.sesiones_semana),
        minutos: num(s.minutos),
        cuota: num(s.cuota),
        horas_prof: num(s.horas_prof),
      }));

      const payload = {
        student_id: user.id,
        name: state.name || "Sin nombre",
        servicios_grupo,
        servicios_individuales,
        empleados: state.empleados,
        gastos: state.gastos,
        seg_social_pct: num(state.seg_social_pct) / 100,
        visitas_semana: num(state.visitas_semana),
        semanas_mes: num(state.semanas_mes),
        updated_at: new Date().toISOString(),
      };

      if (currentId) {
        const { error } = await supabase.from("simulations").update(payload).eq("id", currentId);
        if (error) throw error;
      } else {
        const { data, error } = await supabase.from("simulations").insert(payload).select("id").single();
        if (error) throw error;
        setCurrentId(data.id);
      }
      await refetch();
      showSuccess("Simulación guardada correctamente");
      setView("list");
    } catch (err) {
      showError("No se pudo guardar la simulación. Inténtalo de nuevo.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSkeleton />;

  if (view === "list") {
    return (
      <SimulationsList
        simulations={simulations}
        onNew={handleNew}
        onSelect={handleSelect}
        onDelete={handleDelete}
        onDuplicate={handleDuplicate}
        onBack={onBack}
      />
    );
  }

  return (
    <SimulationEditor
      state={state}
      setState={setState}
      onSave={handleSave}
      onCancel={() => setView("list")}
      saving={saving}
    />
  );
}
